import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Package, Tag, Truck, Upload, LogOut, User, Users, Palette, Factory, Settings } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import ExchangeRatesWidget from './ExchangeRatesWidget';

const Navigation = () => {
  const navigate = useNavigate();
  const { user, isAdmin, isColorsOnly, logout } = useAuth();
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  const adminMenuRef = useRef(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    if (!showAdminMenu) return;
    const handler = (e) => {
      if (adminMenuRef.current && !adminMenuRef.current.contains(e.target)) {
        setShowAdminMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showAdminMenu]);

  const linkStyle = (isActive) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    padding: '5px 10px',
    borderRadius: '6px',
    color: isActive ? '#3b82f6' : '#6b7280',
    backgroundColor: isActive ? '#eff6ff' : 'transparent',
    textDecoration: 'none',
    fontWeight: isActive ? '600' : '500',
    fontSize: '16px',
    whiteSpace: 'nowrap',
    transition: 'all 0.15s',
  });

  return (
    <nav style={{ background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', position: 'sticky', top: 0, zIndex: 500 }}>
      <div style={{ maxWidth: '1600px', margin: '0 auto', padding: '0 16px', display: 'flex', alignItems: 'center', gap: '4px', height: '52px' }}>

        {/* Бренд */}
        <NavLink to="/" style={{ fontSize: '15px', fontWeight: '800', color: '#1f2937', textDecoration: 'none', letterSpacing: '-0.5px', marginRight: '8px', whiteSpace: 'nowrap' }}>
          StockFlow Artique
        </NavLink>

        {/* Основные разделы */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '2px', flex: 1 }}>
          {isAdmin && (
            <>
              <NavLink to="/products" style={({ isActive }) => linkStyle(isActive)}>
                <Package size={14} /> Товары
              </NavLink>
              <NavLink to="/brands" style={({ isActive }) => linkStyle(isActive)}>
                <Tag size={14} /> Бренды
              </NavLink>
              <NavLink to="/suppliers" style={({ isActive }) => linkStyle(isActive)}>
                <Truck size={14} /> Поставщики
              </NavLink>
              <NavLink to="/factories" style={({ isActive }) => linkStyle(isActive)}>
                <Factory size={14} /> Фабрики
              </NavLink>
            </>
          )}
          {!isColorsOnly && (
            <NavLink to="/upload" style={({ isActive }) => linkStyle(isActive)}>
              <Upload size={14} /> Загрузка
            </NavLink>
          )}
          <NavLink to="/colors" style={({ isActive }) => linkStyle(isActive)}>
            <Palette size={14} /> Цвета
          </NavLink>

          {/* Система: Задачи + Расписание + Пользователи */}
          {isAdmin && (
            <div ref={adminMenuRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setShowAdminMenu(v => !v)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '5px',
                  padding: '5px 10px', borderRadius: '6px',
                  color: showAdminMenu ? '#3b82f6' : '#6b7280',
                  backgroundColor: showAdminMenu ? '#eff6ff' : 'transparent',
                  border: 'none', cursor: 'pointer',
                  fontWeight: '500', fontSize: '16px', whiteSpace: 'nowrap',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { if (!showAdminMenu) e.currentTarget.style.color = '#3b82f6'; }}
                onMouseLeave={e => { if (!showAdminMenu) e.currentTarget.style.color = '#6b7280'; }}
              >
                <Settings size={14} />
                Система
                <span style={{ fontSize: '9px', opacity: 0.5, marginLeft: '1px' }}>{showAdminMenu ? '▲' : '▼'}</span>
              </button>

              {showAdminMenu && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 1000,
                  background: 'white', border: '1px solid #e5e7eb', borderRadius: '10px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.1)', overflow: 'hidden', minWidth: '160px',
                }}>
                  {[
                    { to: '/tasks', icon: <Package size={14} />, label: 'Задачи' },
                    { to: '/schedule', icon: <Upload size={14} />, label: 'Расписание' },
                    { to: '/users', icon: <Users size={14} />, label: 'Пользователи' },
                    { to: '/bot-users', icon: <Users size={14} />, label: 'Бот: доступ' },
                  ].map(({ to, icon, label }) => (
                    <NavLink key={to} to={to}
                      onClick={() => setShowAdminMenu(false)}
                      style={({ isActive }) => ({
                        display: 'flex', alignItems: 'center', gap: '10px',
                        padding: '10px 14px', textDecoration: 'none',
                        color: isActive ? '#3b82f6' : '#374151',
                        fontWeight: isActive ? '600' : '500',
                        fontSize: '13px', backgroundColor: isActive ? '#eff6ff' : 'transparent',
                        borderBottom: '1px solid #f3f4f6',
                        transition: 'background 0.1s',
                      })}
                      onMouseEnter={e => { if (!e.currentTarget.classList.contains('active')) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                      onMouseLeave={e => { if (!e.currentTarget.classList.contains('active')) e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      {icon} {label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Правая часть: курсы + пользователь */}
        {isAdmin && (
          <div style={{ marginRight: '8px' }}>
            <ExchangeRatesWidget compact={true} />
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <span style={{ fontSize: '13px', color: '#374151', fontWeight: '500', whiteSpace: 'nowrap' }}>
            <User size={13} style={{ verticalAlign: 'middle', marginRight: '3px' }} />
            {user.username}
            <span style={{ color: '#9ca3af', fontWeight: '400', fontSize: '12px', marginLeft: '4px' }}>
              {isAdmin ? 'admin' : isColorsOnly ? 'цвета' : 'user'}
            </span>
          </span>
          <button onClick={handleLogout} title="Выход"
            style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: '5px', display: 'flex', alignItems: 'center', borderRadius: '4px', transition: 'all 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.backgroundColor = '#fef2f2'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#9ca3af'; e.currentTarget.style.backgroundColor = 'transparent'; }}>
            <LogOut size={15} />
          </button>
        </div>

      </div>
    </nav>
  );
};

export default Navigation;
